import React, { useState, useEffect } from 'react';
import styles from '../../WizardSteps.module.css';
import choicesStyles from './HomeChoicesStep.module.css';
import { formatBaseFloorName } from '../../../../HomePage/OverviewPage/SpacesStructure/SpacesStructure';

// Classify an item based on its options structure (reused from HomeChoicesSection)
const getItemCategory = masterItem => {
  if (masterItem.is_bundle && masterItem.bundle_type === 'choice') return 'choice_bundle';
  const options = masterItem.item_options?.options;
  if (!options || !Array.isArray(options) || options.length === 0) return 'no_options';
  const groups = [...new Set(options.map(o => o.group).filter(Boolean))];
  return groups.length > 1 ? 'multi_group' : 'single_group';
};

// Group options by their group field (reused from HomeChoicesSection)
const groupOptionsByGroup = rawOptions => {
  if (!rawOptions || !Array.isArray(rawOptions)) return {};
  const grouped = {};
  rawOptions.forEach(opt => {
    const group = opt.group || 'default';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(opt);
  });
  return grouped;
};

const HomeChoicesStep = ({
  data,
  onChange,
  homeChoiceItems = [],
  floorsData = {},
  bundleChildItems = [],
}) => {
  const [selections, setSelections] = useState(data?.selections || {});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedFloors, setSelectedFloors] = useState({}); // Track floor selections for floor-specific items

  const floors = floorsData?.floors || [];

  // Update parent when selections change
  useEffect(() => {
    onChange({ selections });
  }, [selections, onChange]);

  // Sort items by display_order, with whole_home items first
  const sortedItems = [...homeChoiceItems].sort((a, b) => {
    // First sort by scope - whole_home before floor_specific
    if (a.scope !== b.scope) {
      return a.scope === 'whole_home' ? -1 : 1;
    }
    // Then by display_order
    if (a.display_order !== null && b.display_order !== null) {
      return a.display_order - b.display_order;
    }
    return (a.master_item?.item_name || '').localeCompare(b.master_item?.item_name || '');
  });

  const currentItem = sortedItems[currentIndex];
  const currentCategory = currentItem
    ? getItemCategory(currentItem.master_item || {})
    : 'no_options';
  const currentOptionGroups = currentItem
    ? groupOptionsByGroup(currentItem.master_item?.item_options?.options || [])
    : {};
  const groupNames = Object.keys(currentOptionGroups);
  const isLastItem = currentIndex >= sortedItems.length - 1;
  const hasItems = sortedItems.length > 0;
  const isFloorSpecific = currentItem?.scope === 'floor_specific';
  const isChoiceBundle = currentCategory === 'choice_bundle';
  const isMultiGroup = currentCategory === 'multi_group';

  // For choice bundles, build children list from bundle_items + bundleChildItems
  const choiceBundleChildren = isChoiceBundle
    ? (currentItem.master_item.bundle_items?.items || [])
        .map(child => bundleChildItems.find(bc => bc.id === child.item_id))
        .filter(Boolean)
    : [];

  // Get current floor selection for this item
  const currentFloorSelection = selectedFloors[currentItem?.master_item_id] || {
    allFloors: true,
    floorIds: [],
  };

  // Handle floor checkbox changes
  const handleFloorToggle = floorId => {
    const itemId = currentItem?.master_item_id;
    if (!itemId) return;

    setSelectedFloors(prev => {
      const current = prev[itemId] || { allFloors: true, floorIds: [] };

      if (floorId === 'all') {
        // Toggle "all floors"
        return {
          ...prev,
          [itemId]: { allFloors: !current.allFloors, floorIds: [] },
        };
      } else {
        // Toggle specific floor
        const newFloorIds = current.floorIds.includes(floorId)
          ? current.floorIds.filter(id => id !== floorId)
          : [...current.floorIds, floorId];
        return {
          ...prev,
          [itemId]: { allFloors: false, floorIds: newFloorIds },
        };
      }
    });
  };

  // Handle choice bundle child selection
  const handleSelectBundleChild = (childId, childName) => {
    if (!currentItem) return;

    const floorSelection = isFloorSpecific
      ? currentFloorSelection.allFloors
        ? { allFloors: true }
        : { floorIds: currentFloorSelection.floorIds }
      : null;

    setSelections(prev => ({
      ...prev,
      [currentItem.master_item_id]: {
        name: childName,
        selected_child_id: childId,
        itemName: currentItem.master_item?.item_name,
        scope: currentItem.scope,
        ...(floorSelection && { floors: floorSelection }),
      },
    }));

    if (!isLastItem) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Handle option selection — stores per-group and auto-advances when all groups selected
  const handleSelect = (groupName, optionName) => {
    if (!currentItem) return;

    const floorSelection = isFloorSpecific
      ? currentFloorSelection.allFloors
        ? { allFloors: true }
        : { floorIds: currentFloorSelection.floorIds }
      : null;

    setSelections(prev => {
      const existing = prev[currentItem.master_item_id] || {};
      const existingGroups = existing.groups || {};
      const updatedGroups = { ...existingGroups, [groupName]: optionName };

      return {
        ...prev,
        [currentItem.master_item_id]: {
          ...existing,
          groups: updatedGroups,
          itemName: currentItem.master_item?.item_name,
          scope: currentItem.scope,
          ...(floorSelection && { floors: floorSelection }),
        },
      };
    });

    // Auto-advance: for single-group items advance immediately,
    // for multi-group items advance only when all groups have a selection
    if (!isMultiGroup) {
      if (!isLastItem) setCurrentIndex(prev => prev + 1);
    } else {
      // Check if all groups now have a selection (including the one just set)
      const existing = selections[currentItem.master_item_id]?.groups || {};
      const updated = { ...existing, [groupName]: optionName };
      const allGroupsSelected = groupNames.every(g => updated[g]);
      if (allGroupsSelected && !isLastItem) {
        setCurrentIndex(prev => prev + 1);
      }
    }
  };

  // Skip current item — advance to next, or stay on last (user clicks Continue to proceed)
  const handleSkip = () => {
    if (currentIndex < sortedItems.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  // Go back to previous item
  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Edit a previously set selection
  const handleEdit = index => {
    setCurrentIndex(index);
  };

  // Get list of completed selections
  const completedSelections = Object.entries(selections).map(([masterItemId, selection]) => ({
    masterItemId,
    ...selection,
  }));

  return (
    <div className={styles.stepContainer}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>העדפות לבית</h1>
        <p className={styles.subtitle}>בחר העדפות לפריטים נפוצים</p>
      </div>

      {/* Info Notes */}
      <div className={choicesStyles.infoBox}>
        <ul className={choicesStyles.infoList}>
          <li>הגדרת ברירת מחדל תעזור להעריך עלויות בקלות</li>
          <li>ניתן לשנות ברירות מחדל בכל עת</li>
          <li>ניתן לשנות פריט ספציפי בפרויקט בנפרד</li>
        </ul>
      </div>

      {/* Skip All Badge */}
      {hasItems && (
        <div className={choicesStyles.skipAllBadge}>
          שלב אופציונלי — לחץ &quot;המשך&quot; כדי לדלג על כל הפריטים
        </div>
      )}

      {!hasItems ? (
        <div className={choicesStyles.emptyState}>
          <p>אין פריטים להגדרה</p>
        </div>
      ) : (
        <>
          {/* Progress indicator */}
          <div className={choicesStyles.progressIndicator}>
            <span>
              {currentIndex + 1} / {sortedItems.length}
            </span>
          </div>

          {/* Current Item Card */}
          {currentItem && (
            <div className={choicesStyles.itemCard}>
              <h3 className={choicesStyles.itemName}>{currentItem.master_item?.item_name}</h3>
              <p className={choicesStyles.itemQuestion}>האם תרצה להגדיר ברירת מחדל?</p>

              {/* Floor Selection for floor-specific items (hide when only 1 floor) */}
              {isFloorSpecific && floors.length > 1 && (
                <div className={choicesStyles.floorSelection}>
                  <p className={choicesStyles.floorLabel}>לאילו קומות?</p>
                  <div className={choicesStyles.floorCheckboxes}>
                    <label className={choicesStyles.floorCheckbox}>
                      <input
                        type="checkbox"
                        checked={currentFloorSelection.allFloors}
                        onChange={() => handleFloorToggle('all')}
                      />
                      <span>כל הקומות</span>
                    </label>
                    {floors.map(floor => (
                      <label key={floor.id} className={choicesStyles.floorCheckbox}>
                        <input
                          type="checkbox"
                          checked={
                            !currentFloorSelection.allFloors &&
                            currentFloorSelection.floorIds.includes(floor.id)
                          }
                          disabled={currentFloorSelection.allFloors}
                          onChange={() => handleFloorToggle(floor.id)}
                        />
                        <span>{formatBaseFloorName(floor.floorNum)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Options — choice bundle children, multi-group, or single-group */}
              {isChoiceBundle && choiceBundleChildren.length > 0 ? (
                <div className={choicesStyles.optionsGrid}>
                  {choiceBundleChildren.map(child => {
                    const isSelected =
                      selections[currentItem.master_item_id]?.selected_child_id === child.id;
                    return (
                      <button
                        key={child.id}
                        type="button"
                        className={`${choicesStyles.optionButton} ${isSelected ? choicesStyles.selected : ''}`}
                        onClick={() => handleSelectBundleChild(child.id, child.item_name)}
                      >
                        <span className={choicesStyles.optionName}>{child.item_name}</span>
                        {child.estimated_cost > 0 && (
                          <span className={choicesStyles.optionCost}>
                            ₪{child.estimated_cost.toLocaleString()}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : isMultiGroup ? (
                // Render each option group with a label
                Object.entries(currentOptionGroups).map(([groupName, options]) => (
                  <div key={groupName} style={{ marginBottom: '12px' }}>
                    <p className={choicesStyles.floorLabel}>{groupName}</p>
                    <div className={choicesStyles.optionsGrid}>
                      {options.map(option => {
                        const isSelected =
                          selections[currentItem.master_item_id]?.groups?.[groupName] ===
                          option.name;
                        return (
                          <button
                            key={option.name}
                            type="button"
                            className={`${choicesStyles.optionButton} ${isSelected ? choicesStyles.selected : ''}`}
                            onClick={() => handleSelect(groupName, option.name)}
                          >
                            <span className={choicesStyles.optionName}>{option.name}</span>
                            {option.default_cost != null && (
                              <span className={choicesStyles.optionCost}>
                                ₪{option.default_cost.toLocaleString()}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              ) : (
                // Single-group: flat grid
                <div className={choicesStyles.optionsGrid}>
                  {Object.entries(currentOptionGroups).flatMap(([groupName, options]) =>
                    options.map(option => {
                      const isSelected =
                        selections[currentItem.master_item_id]?.groups?.[groupName] === option.name;
                      return (
                        <button
                          key={option.name}
                          type="button"
                          className={`${choicesStyles.optionButton} ${isSelected ? choicesStyles.selected : ''}`}
                          onClick={() => handleSelect(groupName, option.name)}
                        >
                          <span className={choicesStyles.optionName}>{option.name}</span>
                          {option.default_cost != null && (
                            <span className={choicesStyles.optionCost}>
                              ₪{option.default_cost.toLocaleString()}
                            </span>
                          )}
                        </button>
                      );
                    }),
                  )}
                </div>
              )}

              {/* Item Navigation */}
              <div className={choicesStyles.itemNav}>
                {currentIndex > 0 && (
                  <button type="button" className={choicesStyles.navButton} onClick={handleBack}>
                    הקודם →
                  </button>
                )}
                <button type="button" className={choicesStyles.skipButton} onClick={handleSkip}>
                  ← דלג
                </button>
              </div>
            </div>
          )}

          {/* Done message when at last item - shown above the list */}
          {isLastItem && currentIndex === sortedItems.length - 1 && (
            <div className={choicesStyles.doneMessage}>
              <p>סיימת לעבור על כל הפריטים! לחץ המשך כדי להמשיך.</p>
            </div>
          )}

          {/* Completed Selections List */}
          {completedSelections.length > 0 && (
            <div className={choicesStyles.completedSection}>
              <h4 className={choicesStyles.completedTitle}>הבחירות שלך</h4>
              <div className={choicesStyles.completedList}>
                {completedSelections.map(selection => {
                  const itemIndex = sortedItems.findIndex(
                    item => item.master_item_id === selection.masterItemId,
                  );
                  return (
                    <div
                      key={selection.masterItemId}
                      className={choicesStyles.completedItem}
                      onClick={() => handleEdit(itemIndex)}
                    >
                      <span className={choicesStyles.completedItemName}>{selection.itemName}</span>
                      <span className={choicesStyles.completedItemValue}>
                        {selection.selected_child_id
                          ? selection.name
                          : selection.groups
                            ? Object.values(selection.groups).join(', ')
                            : selection.name || ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default HomeChoicesStep;
